import React, { useEffect, useState } from 'react';
import {Table, Input, Space, Tag, Select, Empty, Typography, Button, message} from 'antd';
import axios from 'axios';
import { TaskData } from '../../../../types/TaskData';
import './ConstructionOrderTable.css';
import {FilePdfOutlined} from "@ant-design/icons";
import {StatusUtils} from "../../../../utils/StatusUtils";
import {getConfig} from "../../../../config";
import LoadingModal from "../../../../components/loadingModal/LoadingModal";
import {useProjectContext} from "../../../../context/ProjectContext";

const { Option } = Select;
const { Text } = Typography;
const { API_BASE_URL } = getConfig();

interface ConstructionOrderTableProps {
    onSelectRows: (rows: TaskData[]) => void;
    refreshKey: number;
}

const ConstructionOrderTable: React.FC<ConstructionOrderTableProps> = ({ onSelectRows, refreshKey }) => {
    const [data, setData] = useState<TaskData[]>([]);
    const { selectedProject, selectedManagerNo } = useProjectContext();
    const [originalData, setOriginalData] = useState<TaskData[]>([]); // To store the original data
    const [showAllData, setShowAllData] = useState(false); // Checkbox state
    const [selectedRows, setSelectedRows] = useState<TaskData[]>([]);
    const [globalSearch, setGlobalSearch] = useState('');
    const [arraysEqual, setArraysEqual] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    useEffect(() => {
        if (selectedProject)    {
            fetchData(selectedProject);
        }
    }, [selectedProject, refreshKey]);

    const fetchData = (projectNo: string) => {
        axios
            .get<TaskData[]>(`${API_BASE_URL}/getReadyToDoTasks?auth=VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU&projectNo=${projectNo}&type=construction&managerNo=${selectedManagerNo}`)
            .then(response => {
                setOriginalData(response.data); // Store original data
                setData(showAllData ? response.data : response.data.filter(item => item.activityStatus === 'READY')); // Filter by READY if checkbox is unchecked
                setArraysEqual(
                    JSON.stringify(originalData) === JSON.stringify(data)
                );
            })
            .catch(error => console.error('Error fetching data:', error));
        setSelectedRows([]);
    };


    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        setShowAllData(checked);
        setData(checked ? originalData : originalData.filter(item => item.activityStatus === 'READY'));
    };

    const filteredData = data.filter(item =>
        item.orderCardId.toLowerCase().includes(globalSearch.toLowerCase()) ||
        (item.jobCodeDesc?.toLowerCase() ?? '').includes(globalSearch.toLowerCase()) ||
        (item.oglDesc?.toLowerCase() ?? '').includes(globalSearch.toLowerCase()) ||
        item.faz.toString().includes(globalSearch)
    );

    const fetchPDFAndOpen = async (orderCardId: string) => {
        setLoadingText("PDF wird geladen...");
        setLoading(true)
        try {
            const projectId = parseInt(orderCardId.slice(0, 6), 10); // First 6 digits
            const supplement = parseInt(orderCardId.slice(6, 8), 10); // Next 2 digits
            const protocol = parseInt(orderCardId.slice(8, 10), 10); // Last 2 digits
            const response = await axios.get(
                `${API_BASE_URL}/getOrderCardByID`, {
                    params: {
                        auth: "VdjCKidks7qTEyuU6QeJtezZ3PeCHhZQ2yHJ3kPtcfTN8BjVqU",
                        projectId,
                        supplement,
                        protocol
                    }
                }
            );

            const pdfBase64 = response.data.backendMessage.split(',')[1]; // Extract the base64 PDF
            const pdfWindow = window.open('', '_blank');
            pdfWindow?.document.write(
                `<iframe width="100%" height="100%" src="data:application/pdf;base64,${pdfBase64}"></iframe>`
            );
        } catch (error) {
            message.error('Fehler beim Abrufen des PDFs.');
            console.error('Error fetching PDF:', error);
        } finally {
            setLoading(false)
        }
    };


    const getSelectableCriteria = (): { jobCodeDesc: string | null; korrex: string | null } | null => {
        if (selectedRows.length === 0) return null;
        return {
            jobCodeDesc: selectedRows[0].jobCodeDesc,
            korrex: selectedRows[0].korrex,
        };
    };

    const isRowSelectable = (record: TaskData): boolean => {
        const criteria = getSelectableCriteria();
        if (!criteria) return true; // Allow selection if no row is selected yet
        return (
            record.jobCodeDesc === criteria.jobCodeDesc &&
            record.korrex === criteria.korrex
        );
    };


    const columns = [
        {
            title: 'Auftragskarte',
            dataIndex: 'orderCardId',
            key: 'orderCardId',
            sorter: (a: { orderCardId: string }, b: { orderCardId: string }) => a.orderCardId.localeCompare(b.orderCardId),
            render: (orderCardId: string) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Button
                        icon={<FilePdfOutlined />}
                        onClick={(event) => {
                            event.stopPropagation(); // Prevent row selection on button click
                            fetchPDFAndOpen(orderCardId);
                        }}
                        type="primary"
                        size="small"
                    >
                        {orderCardId.replace(/(\d{6})(\d{2})(\d{2})/, '$1/$2/$3')}
                    </Button>
                </div>
            ),
        },
        {
            title: 'Produkt',
            dataIndex: 'jobCodeDesc',
            key: 'jobCodeDesc',
            sorter: (a: TaskData, b: TaskData) => (a.jobCodeDesc ?? '').localeCompare(b.jobCodeDesc ?? ''),
            render: (text: string, record: TaskData) => (
                <>
                    {text}
                    {record.korrex && ` (Korrex)`}
                </>
            ),
        },
        {
            title: 'Bauabschnitt',
            dataIndex: 'oglDesc',
            key: 'oglDesc',
            sorter: (a: { oglDesc: any; }, b: { oglDesc: any; }) => (a.oglDesc || '').localeCompare(b.oglDesc || '')
        },
        {
            title: 'Protokoll-Bez.',
            dataIndex: 'protocolDesc',
            key: 'protocolDesc',
            sorter: (a: TaskData, b: TaskData) => {
                const valueA = a.protocolDesc?.substring(6) ?? '';
                const valueB = b.protocolDesc?.substring(6) ?? '';
                return valueA.localeCompare(valueB);
            },
            render: (text: string) => {
                return text?.substring(6) ?? '';
            },
            width: 250,
        },

        {
            title: 'Start',
            dataIndex: 'faz',
            key: 'faz',
            sorter: (a: { faz: string }, b: { faz: string }) => {
                // Sort using string comparison of ISO dates (which works because they are in "YYYY-MM-DD" format)
                return a.faz.localeCompare(b.faz);
            },
            render: (faz: string) => {
                // Convert "YYYY-MM-DD" to "DD.MM.YYYY"
                const [year, month, day] = faz.split('-');
                return `${day}.${month}.${year}`;
            },
        },
        {
            title: 'Status',
            dataIndex: 'activityStatus',
            key: 'activityStatus',
            sorter: (a: TaskData, b: TaskData) => (a.activityStatus ?? '').localeCompare(b.activityStatus ?? ''),
            render: (status: 'DONE' | 'READY' | 'NOT_READY' | 'PENDING') => {
                const color = StatusUtils.getColor(status);
                const translatedStatus = StatusUtils.getTranslatedStatus(status);
                return <Tag color={color}>{translatedStatus}</Tag>;
            },
        }
    ];

    const rowSelection = {
        onChange: (selectedRowKeys: React.Key[], selectedRows: TaskData[]) => {
            setSelectedRows(selectedRows);
            onSelectRows(selectedRows);
        },
        getCheckboxProps: (record: TaskData) => ({
            disabled: !isRowSelectable(record),
        }),
        selectedRowKeys: selectedRows.map(row => row.id),
        columnTitle: null, // <-- Versteckt die Checkbox im Header
    };


    const handleRowClick = (record: TaskData) => {
        if (record.activityStatus !== 'READY') {
            return; // Prevent interaction for non-READY rows
        }
        const isSelected = selectedRows.some(row => row.id === record.id);
        const newSelectedRows = isSelected
            ? selectedRows.filter(row => row.id !== record.id) // Remove if already selected
            : [...selectedRows, record]; // Add to selection if not already selected

        setSelectedRows(newSelectedRows);
        onSelectRows(newSelectedRows);
    };


    return (
        <div style={{ padding: '20px' }}>
            <LoadingModal
                open={loading}
                text={loadingText}
            />
            <Space
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'flex-end', // Aligns items to the left
                    width: '100%',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <label>
                        <input
                            type="checkbox"
                            checked={showAllData}
                            disabled={!arraysEqual}
                            onChange={handleCheckboxChange}
                            style={{ marginRight: '8px' }} // Adds spacing between the checkbox and label
                        />
                        Alle Daten für 'Konstruktionsbeauftragung' anzeigen
                    </label>
                </div>
            </Space>


            {/* Global Search Input */}
            <Input
                placeholder="Suche in Auftragskarte, Produkt, Bauabschnitt oder Start"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                style={{ marginBottom: 16, width: '100%' }}
            />

            {/* Table with row selection */}
            <Table
                rowSelection={{
                    type: 'checkbox',
                    ...rowSelection,
                }}
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                pagination={{ pageSize: 100 }}
                scroll={{ y: 700 }}
                locale={{
                    emptyText: <Empty description="Keine Auftragskarten zur Beauftragung vorhanden" />
                }}
                rowClassName={(record) =>
                    isRowSelectable(record)
                        ? selectedRows.includes(record)
                            ? 'selected-row'
                            : ''
                        : 'disabled-row'
                }
                onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                })}
            />
            {selectedRows.length > 1 && (
                <Text type="secondary" style={{ marginTop: 16 }}>
                    Es werden {selectedRows.length} Auftragskarten zusammengefasst
                </Text>
            )}
        </div>
    );
};

export default ConstructionOrderTable;
